import React from 'react';
import { Phone, Mail, MapPin, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Helmet } from 'react-helmet-async';

const TermsConditionsPage = () => {
  const handlePrint = () => {
    window.print();
  };

  return (
    <>
      <Helmet>
        <title>Terms & Conditions | SHIPPZY</title>
        <meta name="description" content="Read Shippzy's Terms and Conditions for international shipping services from Canada. Understand our policies and service agreements." />
        <link rel="canonical" href="https://www.shippzy.ca/terms-conditions" />
      </Helmet>
      
      <div className="min-h-screen bg-white">
      {/* Print Button - Hidden in Print */}
      <div className="no-print fixed top-4 right-4 z-50">
        <Button 
          onClick={handlePrint}
          className="bg-gradient-to-r from-red-600 to-yellow-500 hover:from-red-700 hover:to-yellow-600 text-white shadow-lg"
        >
          <Download className="w-4 h-4 mr-2" />
          Download PDF
        </Button>
      </div>

      {/* Document Container */}
      <div className="max-w-4xl mx-auto p-8 md:p-12">
        {/* Letterhead Header */}
        <div className="border-b-4 border-red-600 pb-6 mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_angry-ellis-7/artifacts/rd8nrfx9_IMG_7688.png" 
                alt="Shippzy Logo" 
                className="h-16 w-16 rounded-full"
              />
              <div>
                <h1 className="text-3xl font-bold text-gray-900">SHIPPZY INC.</h1>
                <p className="text-yellow-600 font-semibold">Ship with Shippzy</p>
              </div>
            </div>
          </div>
          <div className="mt-4 text-sm text-gray-600 space-y-1">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              <span>140 Advance Blvd, Unit 7, Brampton, ON L6T 4Z8, Canada</span>
            </div>
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4" />
              <span>+1 (437) 616-0758</span>
            </div>
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              <span>ship@shippzy.ca</span>
            </div>
          </div>
        </div>

        {/* Document Content */}
        <div className="space-y-6">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">TERMS & CONDITIONS</h2>
            <p className="text-gray-600">Last Updated: March 6, 2026</p>
          </div>

          <div className="prose max-w-none">
            <p className="text-gray-700 leading-relaxed">
              These Terms and Conditions govern the use of services provided by Shippzy Inc. ("Shippzy", "we", "our", or "us"). By using the services of Shippzy, including accessing the website Shippzy.ca, submitting a shipment, or arranging logistics services, you agree to comply with these Terms and Conditions.
            </p>

            <p className="text-gray-700 leading-relaxed font-semibold">
              If you do not agree with these terms, you must not use our services.
            </p>

            <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">1. Nature of Services</h3>
            <p className="text-gray-700 leading-relaxed">
              Shippzy provides logistics facilitation services including but not limited to:
            </p>
            <ul className="list-disc ml-6 text-gray-700 space-y-2">
              <li>Domestic and international shipping arrangements</li>
              <li>Courier service coordination</li>
              <li>Shipment documentation preparation</li>
              <li>Pickup and drop-off coordination</li>
              <li>Customs clearance facilitation</li>
              <li>Shipping label generation and tracking services</li>
            </ul>

            <p className="text-gray-700 leading-relaxed mt-4">
              Shippzy operates as an independent logistics service provider and may arrange shipments using third-party carriers. These carriers may include companies such as DHL, FedEx, UPS, and Purolator.
            </p>

            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 my-4">
              <p className="text-gray-800 font-semibold">Important Notice:</p>
              <p className="text-gray-700">
                Use of these carriers does not imply Shippzy is an authorized dealer or official agent of any carrier unless explicitly stated.
              </p>
            </div>

            <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">2. Shipment Processing</h3>
            <p className="text-gray-700 leading-relaxed">
              Customers must provide accurate shipment details including:
            </p>
            <ul className="list-disc ml-6 text-gray-700 space-y-1">
              <li>Sender information</li>
              <li>Recipient information</li>
              <li>Shipment contents</li>
              <li>Package weight and dimensions</li>
              <li>Declared value</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4 font-semibold">
              Incorrect information may result in delays, additional charges, customs penalties, or shipment refusal.
            </p>

            <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">3. Dimensional Weight</h3>
            <p className="text-gray-700 leading-relaxed">
              Shipping charges may be calculated using dimensional weight.
            </p>
            <div className="bg-gray-100 p-4 rounded-lg my-4">
              <p className="text-gray-900 font-semibold mb-2">Dimensional weight formula used:</p>
              <p className="text-gray-800 text-lg">Length × Width × Height (cm) ÷ 5000</p>
            </div>
            <p className="text-gray-700 leading-relaxed font-semibold">
              The greater of actual weight or dimensional weight will be charged.
            </p>

            <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">4. Refund Policy</h3>
            
            <h4 className="font-semibold text-gray-900 mb-2">Shipment Cancellation Before Pickup</h4>
            <p className="text-gray-700 leading-relaxed">
              Customers may receive a full refund if the shipment is cancelled before pickup.
            </p>
            <p className="text-gray-700 leading-relaxed">
              However, if the pickup location is outside a 20 km radius, Shippzy reserves the right to deduct:
            </p>
            <ul className="list-disc ml-6 text-gray-700 space-y-1">
              <li>Fuel costs</li>
              <li>Driver labour costs</li>
              <li>Pickup service charges</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-2">The remaining balance will be refunded.</p>

            <h4 className="font-semibold text-gray-900 mb-2 mt-4">Shipment After Pickup or Shipment Processing</h4>
            <p className="text-gray-700 leading-relaxed">
              Once a shipment has been picked up, processed, or handed over to a carrier, refunds will be subject to the rules and policies of the carrier handling the shipment.
            </p>
            <p className="text-gray-700 leading-relaxed font-semibold">
              Shippzy reserves the right to deny refunds where services have already been rendered.
            </p>

            <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">5. Insurance Policy</h3>
            <div className="bg-red-50 border-l-4 border-red-500 p-4 my-4">
              <p className="text-gray-800 font-semibold">Shipping insurance is not automatically included with any shipment arranged through Shippzy.</p>
            </div>
            <p className="text-gray-700 leading-relaxed">
              Customers must request insurance before shipment processing.
            </p>
            <p className="text-gray-700 leading-relaxed font-semibold">
              If insurance is not purchased, the shipment is transported at the customer's risk. Shippzy assumes zero liability for loss, damage, or delay.
            </p>

            <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">6. Liability Disclaimer</h3>
            <p className="text-gray-700 leading-relaxed font-semibold">
              Shippzy acts solely as a logistics facilitator.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Shippzy shall not be liable for loss, damage, or delay caused by:
            </p>
            <ul className="list-disc ml-6 text-gray-700 space-y-2">
              <li>Third-party courier companies</li>
              <li>Airline cargo carriers</li>
              <li>Customs authorities</li>
              <li>Weather conditions</li>
              <li>Airport handling</li>
              <li>Transportation delays</li>
              <li>Government inspections</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              All claims related to loss or damage must be handled in accordance with the policies of the carrier responsible for transporting the shipment.
            </p>

            <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">7. Customs Duties and Import Taxes</h3>
            <p className="text-gray-700 leading-relaxed">
              For international shipments, Shippzy may assist with customs clearance and documentation. However:
            </p>
            <ul className="list-disc ml-6 text-gray-700 space-y-1">
              <li>Customs duties</li>
              <li>Import taxes</li>
              <li>Inspection fees</li>
              <li>Airport handling charges</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              may be imposed by the destination country. These charges are not controlled by Shippzy.
            </p>
            <p className="text-gray-700 leading-relaxed font-semibold">
              Payment responsibility belongs to the sender or the recipient depending on the shipment arrangement.
            </p>
            <p className="text-gray-700 leading-relaxed mt-4">
              While Shippzy may attempt to facilitate smooth customs clearance and minimize duty where possible, Shippzy does not guarantee any fixed customs duty amount, even where duties are prepaid.
            </p>

            <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">8. Shipment Inspection Rights</h3>
            <p className="text-gray-700 leading-relaxed">
              Shippzy reserves the right to inspect, open, or refuse shipments where necessary for:
            </p>
            <ul className="list-disc ml-6 text-gray-700 space-y-1">
              <li>Safety compliance</li>
              <li>Customs regulations</li>
              <li>Verification of shipment contents</li>
              <li>Suspicion of prohibited items</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              This may be required by carriers, customs authorities, or security agencies.
            </p>

            <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">9. Prohibited Items</h3>
            <p className="text-gray-700 leading-relaxed font-semibold">
              Customers must not ship items that are illegal or restricted under Canadian or international law.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Prohibited items may include but are not limited to:
            </p>
            <div className="grid md:grid-cols-2 gap-2 ml-6 my-4">
              <ul className="list-disc text-gray-700 space-y-1">
                <li>Illegal drugs or narcotics</li>
                <li>Weapons or firearms</li>
                <li>Explosives or hazardous materials</li>
                <li>Cash or currency</li>
                <li>Counterfeit goods</li>
                <li>Dangerous chemicals</li>
              </ul>
              <ul className="list-disc text-gray-700 space-y-1">
                <li>Flammable items</li>
                <li>Restricted batteries</li>
                <li>Alcohol or liquids where restricted</li>
                <li>Perishable goods where prohibited</li>
              </ul>
            </div>
            <p className="text-gray-700 leading-relaxed font-semibold">
              Customers are responsible for verifying shipping legality before sending any item.
            </p>

            <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">10. Shipment Delays</h3>
            <p className="text-gray-700 leading-relaxed">
              Delivery timelines provided by Shippzy are estimates only. Delays may occur due to:
            </p>
            <ul className="list-disc ml-6 text-gray-700 space-y-1">
              <li>Customs clearance</li>
              <li>Airline schedules</li>
              <li>Security inspections</li>
              <li>Weather conditions</li>
              <li>Transportation disruptions</li>
              <li>Carrier delays</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4 font-semibold">
              Shippzy shall not be liable for delays beyond its control.
            </p>

            <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">11. Partner Drop-Off Locations</h3>
            <p className="text-gray-700 leading-relaxed">
              Shippzy may partner with independent businesses to provide parcel drop-off services. These locations act solely as drop-off agents.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Such locations are responsible for shipments only until the package is received by the Shippzy main processing facility or designated carrier.
            </p>
            <p className="text-gray-700 leading-relaxed font-semibold">
              Customers must verify any service promises made by drop-off agents with Shippzy directly or through the official website Shippzy.ca.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Shippzy shall not be responsible for independent representations made by partner locations without verification.
            </p>

            <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">12. Customer Responsibilities</h3>
            <p className="text-gray-700 leading-relaxed">Customers must ensure:</p>
            <ul className="list-disc ml-6 text-gray-700 space-y-1">
              <li>Shipment contents are legal</li>
              <li>Shipment details are accurate</li>
              <li>Package is properly packed</li>
              <li>Correct address is provided</li>
              <li>Customs declarations are accurate</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4 font-semibold">
              Customers are responsible for any penalties or costs resulting from incorrect declarations.
            </p>

            <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">13. Governing Law</h3>
            <p className="text-gray-700 leading-relaxed">
              These Terms and Conditions are governed by the laws of the Province of Ontario, Canada.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Any disputes shall be subject to the jurisdiction of courts located in Ontario.
            </p>

            <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">14. Contact Information</h3>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-gray-900 font-bold mb-2">Shippzy Inc.</p>
              <p className="text-gray-700">140 Advance Blvd, Unit 7, Brampton, Ontario L6T 4Z8, Canada</p>
              <p className="text-gray-700">Email: ship@shippzy.ca</p>
              <p className="text-gray-700">Phone: +1 (437) 616-0758</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t-2 border-gray-300 mt-12 pt-6">
          <div className="flex justify-between items-center text-sm text-gray-600">
            <p>© 2026 Shippzy Inc. All Rights Reserved.</p>
            <p>Document ID: TERMS-2026-03-06</p>
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style jsx>{`
        @media print {
          .no-print {
            display: none !important;
          }
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
        }
      `}</style>
    </div>
    </>
  );
};

export default TermsConditionsPage;
